/**
 * Design tokens for the AWS Monitor design system.
 *
 * Single source of truth for colour, elevation, radius and the chart palette.
 * `theme.ts` feeds these into MUI; the `ui/` component kit reads them directly
 * for the few things MUI's palette cannot express (sidebar, tints, sparklines).
 *
 * Contrast note — every value here was measured, not eyeballed:
 *  - `*.text` variants are the ones safe to put on a `*.tint` background
 *    (all >= 4.0:1) or on white (all >= 4.8:1).
 *  - `*.main` variants are the vibrant ones, used only for non-text marks
 *    (progress fills, status dots, sparkline strokes) where 3:1 is the bar.
 * Never render label text in a `.main` value.
 */

/** Neutral slate ramp — page plane, borders, ink. */
export const neutral = {
  0: '#ffffff',
  25: '#fbfcfe',
  50: '#f6f8fb',
  100: '#eef2f7',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
  950: '#080f1f',
} as const;

/**
 * Semantic roles. `main` = mark colour, `text` = label colour, `tint` = 12%
 * wash used as the chip/soft-button background, `border` = 24% wash.
 */
export const semantic = {
  primary: { main: '#2563eb', dark: '#1d4ed8', light: '#60a5fa', text: '#1d4ed8', tint: '#e5ecfd', border: '#c6d6fa' },
  success: { main: '#16a34a', dark: '#15803d', light: '#4ade80', text: '#15803d', tint: '#e3f4e9', border: '#bfe6cd' },
  warning: { main: '#f59e0b', dark: '#b45309', light: '#fbbf24', text: '#b45309', tint: '#fef3e2', border: '#fadfb4' },
  error:   { main: '#ef4444', dark: '#b91c1c', light: '#f87171', text: '#dc2626', tint: '#fbe5e5', border: '#f5c2c2' },
  info:    { main: '#0891b2', dark: '#0e7490', light: '#22d3ee', text: '#0e7490', tint: '#e1f2f6', border: '#bde3ec' },
  accent:  { main: '#7c3aed', dark: '#6d28d9', light: '#a78bfa', text: '#6d28d9', tint: '#efe7fd', border: '#dbc9fb' },
  neutral: { main: '#64748b', dark: '#475569', light: '#94a3b8', text: '#475569', tint: '#eef2f7', border: '#dbe2ea' },
} as const;

export type SemanticRole = keyof typeof semantic;

/** Dark navy sidebar. */
export const sidebar = {
  bg: '#0a1330',
  bgGradient: 'linear-gradient(180deg, #0e1a3d 0%, #0a1330 48%, #080f27 100%)',
  border: 'rgba(148, 163, 184, 0.12)',
  /** Idle nav label — 7.2:1 on the navy. */
  text: '#a3b1cc',
  textHover: '#eef2fb',
  /** Section captions — 4.6:1 on the navy. */
  caption: '#7485a8',
  activeText: '#ffffff',
  /** Active item: a soft blue wash plus a left accent bar, not a glowing pill. */
  activeBg: 'linear-gradient(90deg, rgba(59,130,246,0.26) 0%, rgba(79,70,229,0.14) 100%)',
  activeBar: '#38D5F5',
  activeIcon: '#7dd3fc',
  activeGlow: 'none',
  hoverBg: 'rgba(148, 163, 184, 0.10)',
  footerBg: 'rgba(255, 255, 255, 0.04)',
} as const;

/**
 * Layered, low-spread shadows. Kept subtle on purpose — the spec asks for
 * "premium, not flashy", and heavy shadows are the first thing that reads cheap.
 */
export const shadows = {
  xs: '0 1px 2px 0 rgba(15, 23, 42, 0.04)',
  sm: '0 1px 3px 0 rgba(15, 23, 42, 0.06), 0 1px 2px -1px rgba(15, 23, 42, 0.04)',
  md: '0 4px 12px -2px rgba(15, 23, 42, 0.07), 0 2px 4px -2px rgba(15, 23, 42, 0.04)',
  lg: '0 12px 28px -8px rgba(15, 23, 42, 0.12), 0 4px 10px -4px rgba(15, 23, 42, 0.05)',
  xl: '0 24px 48px -16px rgba(15, 23, 42, 0.18), 0 8px 16px -8px rgba(15, 23, 42, 0.08)',
} as const;

export const radii = { sm: 6, md: 10, lg: 14, xl: 18, pill: 999 } as const;

/**
 * Chart series palette. This exact order is validated colourblind-safe on a
 * white surface (worst adjacent CVD dE 9.1, normal-vision dE 19.6) — assign
 * slots in order and never cycle or reorder them.
 *
 * Three slots (aqua, yellow, magenta) fall below 3:1 against white, so any
 * chart using them must carry direct labels or an adjacent value column.
 * Every chart in this app does.
 */
export const chartSeries = [
  '#2a78d6', // 1 blue
  '#eb6834', // 2 orange
  '#1baf7a', // 3 aqua
  '#eda100', // 4 yellow
  '#e87ba4', // 5 magenta
  '#008300', // 6 green
  '#4a3aa7', // 7 violet
  '#e34948', // 8 red
] as const;

/** Chart chrome — recessive by design. */
export const chartChrome = {
  grid: '#eef2f7',
  axis: '#cbd5e1',
  label: '#64748b',
  surface: '#ffffff',
} as const;

/**
 * Thresholds that turn a raw utilisation number into a status role.
 * Defined once here so EC2, RDS, S3 and the dashboard never disagree about
 * what "warning" means — three pages deriving it differently is the fastest
 * way to make a good-looking product read as broken.
 */
export const UTILISATION_THRESHOLDS = { warning: 70, critical: 90 } as const;

/** Map a 0-100 utilisation figure to a semantic role. */
export function utilisationRole(value: number | null | undefined): SemanticRole {
  if (value === null || value === undefined || Number.isNaN(value)) return 'neutral';
  if (value >= UTILISATION_THRESHOLDS.critical) return 'error';
  if (value >= UTILISATION_THRESHOLDS.warning) return 'warning';
  return 'success';
}

/** Inverse of the above, for metrics where *low* is bad (free storage, free memory). */
export function headroomRole(value: number | null | undefined): SemanticRole {
  if (value === null || value === undefined || Number.isNaN(value)) return 'neutral';
  if (value <= 100 - UTILISATION_THRESHOLDS.critical) return 'error';
  if (value <= 100 - UTILISATION_THRESHOLDS.warning) return 'warning';
  return 'success';
}

export const layout = {
  sidebarWidth: 264,
  sidebarCollapsedWidth: 76,
  headerHeight: 64,
  pageGutter: { xs: 2, sm: 3 },
  contentMaxWidth: 1800,
} as const;
